import { observer } from 'mobx-react';
import { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { assertNever } from '@kofno/piper';
import EditGroupStore from '../../../../EditGroupStore';
import Main from '../../../../BrowserSupport/Main';
import Notifications from '../../../../Notifications';
import I18nTitle from '../../../../Title/I18nTitle';
import { T } from '../../../../Translations';
import MainNav from '../../../Platform/MainNav';
import Loader from '../../../Loader';
import EditGroupForm from './EditGroupForm';
import ErrorState from './ErrorState';
import EditGroupSuccessModal from './EditGroupSuccessModal';
import UnsavedChangesModal from './UnsavedChangesModal';

/**
 * Edit Group full-page workflow (DVLP-6751). Mirrors the Add Group / Add
 * Participant layout: header strip with title + inline actions, a scrollable
 * region of shared section cards, and a bottom action bar with duplicate
 * buttons. The form itself uses the same shared section components as Add
 * Group; EditGroupStore adapts its state to feed them.
 */
function EditGroupPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const returnUrl = location.state?.returnUrl || '/people/groups';

  if (!id) {
    navigate('/people/groups');
    return null;
  }

  const editGroupStore = useMemo(() => new EditGroupStore(id), [id]);

  const handleNavigateBack = () => navigate(returnUrl);

  const handleCancel = () => {
    if (editGroupStore.hasChanges) {
      editGroupStore.showUnsavedChangesModal();
    } else {
      handleNavigateBack();
    }
  };

  const handleDiscardChanges = () => handleNavigateBack();
  const handleKeepEditing = () => editGroupStore.hideUnsavedChangesModal();

  const { state } = editGroupStore;

  const handleAddParticipants = () => {
    if (state.kind === 'success') {
      navigate(`/people/groups/${state.id}`);
    }
  };

  const groupName = (() => {
    switch (state.kind) {
      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
      case 'updating':
        return state.originalResource.payload.name;
      case 'waiting':
      case 'loading':
      case 'success':
      case 'error':
        return '';
      default:
        return assertNever(state);
    }
  })();

  const content = () => {
    switch (state.kind) {
      case 'waiting':
      case 'loading':
      case 'updating':
      case 'success':
        return <Loader />;
      case 'ready':
      case 'unsaved_changes':
      case 'canceling_with_unsaved_changes':
        return <EditGroupForm editGroupStore={editGroupStore} />;
      case 'error':
        return <ErrorState error={state.error} />;
      default:
        return assertNever(state);
    }
  };

  const primaryButton = (
    <button
      className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ fontWeight: 'var(--font-weight-medium)' }}
      disabled={!editGroupStore.canSubmit}
      onClick={editGroupStore.submit}
      data-intercom-target="edit-group-confirm-button"
    >
      {editGroupStore.isSaving ? <T kind="Saving..." /> : <T kind="Confirm" />}
    </button>
  );

  const cancelButton = (
    <button
      className="px-4 py-2 text-primary hover:opacity-80 transition-opacity"
      style={{ fontWeight: 'var(--font-weight-medium)' }}
      onClick={handleCancel}
      data-intercom-target="edit-group-cancel-button"
    >
      <T kind="Cancel" />
    </button>
  );

  return (
    <>
      <I18nTitle title="Edit Group" />
      <div className="min-h-screen flex flex-col">
        <MainNav />
        <Notifications />
        <div className="border-b border-border bg-background py-6">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl" style={{ fontWeight: 600 }}>
                  <T kind="Edit Group" />
                </h1>
                <p className="text-muted-foreground">
                  <T
                    kind="Configure details, license and collection access for the group: {{groupName}}"
                    groupName={groupName}
                  />
                </p>
              </div>
              <div className="flex items-center gap-3">
                {cancelButton}
                {primaryButton}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-grow bg-muted/30">
          <div className="max-w-5xl mx-auto px-12 py-8">
            <Main id="main">{content()}</Main>
          </div>
        </div>
        <div className="border-t border-border bg-card py-4">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-center justify-between">
              {cancelButton}
              {primaryButton}
            </div>
          </div>
        </div>
        {state.kind === 'canceling_with_unsaved_changes' && (
          <UnsavedChangesModal
            onDiscardChanges={handleDiscardChanges}
            onKeepEditing={handleKeepEditing}
          />
        )}
        {state.kind === 'success' && (
          <EditGroupSuccessModal
            state={state}
            onAddParticipants={handleAddParticipants}
            onClose={handleNavigateBack}
            returnUrl={returnUrl}
          />
        )}
      </div>
    </>
  );
}

export default observer(EditGroupPage);
